import React, { useState, useCallback } from 'react';
import { IndustriesContext } from './IndustriesContext';
import {
	IIndustry,
	getIndustries as fetchIndustries,
} from 'src/services/industries';

const IndustriesProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [industries, setIndustries] = useState<IIndustry[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const getIndustries = useCallback(async (): Promise<IIndustry[]> => {
		// If industries are already loaded, return them
		if (industries.length > 0) {
			return industries;
		}

		// If already loading, wait for the current request
		if (isLoading) {
			// Return a promise that resolves when loading is complete
			return new Promise((resolve, reject) => {
				const checkLoading = () => {
					if (!isLoading) {
						if (error) {
							reject(error);
						} else {
							resolve(industries);
						}
					} else {
						setTimeout(checkLoading, 100);
					}
				};
				checkLoading();
			});
		}

		// Fetch industries from API
		try {
			setIsLoading(true);
			setError(null);

			const industriesData = await fetchIndustries();
			setIndustries(industriesData);

			return industriesData;
		} catch (err) {
			const error =
				err instanceof Error ? err : new Error('Failed to fetch industries');
			console.error('Failed to fetch industries:', error);
			setError(error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [industries, isLoading, error]);

	return (
		<IndustriesContext.Provider
			value={{
				isLoading,
				error,
				getIndustries,
			}}
		>
			{children}
		</IndustriesContext.Provider>
	);
};

export default IndustriesProvider;
